import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ISuggestionItem } from '@cian/ui-kit';
import { IconFunctionalSuccess16 } from '@cian/ui-kit-design-tokens/icons';
import { ISuggestionDefaultItem } from '@cian/ui-kit/suggest/types/ISuggestProps';

import { useApplicationContext } from 'shared/common/utils/applicationContext';

import { useBaseField } from '../components/BaseField';
import { MIN_REGION_SUGGEST_CHARS, REGION_SUGGEST_DEBOUNCE_MS } from '../constants/regionSuggest';
import { setSelectedRegion } from '../redux';
import { selectRegionOptions, selectSelectedRegion } from '../selectors';
import { searchRegions } from '../services/searchRegions';
import { EFieldType, IEFieldSource, IField, ISelectedRegion } from '../types/calculator';
import { useAppDispatch } from './useAppDispatch';

const _findSuggestItemLabelByValue = (value: string | undefined, source: IEFieldSource[] = []) => {
  if (!value) {
    return '';
  }

  const item = source.find(item => item.value === value);

  return item ? item.label : '';
};

const isGeoRegionSuggest = (field: IField) => field.name === 'regionId' && field.type === EFieldType.Suggest;

export const useSuggestField = (props: IField) => {
  const { httpApi, logger } = useApplicationContext();
  const dispatch = useAppDispatch();
  const selectedRegion = useSelector(selectSelectedRegion);
  const regionsOptions = useSelector(selectRegionOptions) ?? [];
  const { field, fieldState } = useBaseField(props);
  const geoMode = isGeoRegionSuggest(props);

  const [isSuggestOpen, setIsSuggestOpen] = useState(false);
  const [inputValue, setInputValue] = useState(_findSuggestItemLabelByValue(String(field.value), props.source));
  const [remoteSuggestions, setRemoteSuggestions] = useState<ISuggestionDefaultItem<string>[]>([]);

  const lastPickedRef = useRef<{ value: string; label: string; parentId: number | null } | null>(null);
  /** id из geo саджеста → parentId (если null — у локации нет родителя). */
  const catalogParentByIdRef = useRef<Map<string, number | null>>(new Map());
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchRequestIdRef = useRef(0);

  const clearSearchTimeout = useCallback(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
  }, []);

  const restoreCommittedDisplay = useCallback(() => {
    const v = String(field.value);

    if (geoMode && selectedRegion && String(selectedRegion.regionId) === v) {
      setInputValue(selectedRegion.displayName);

      return;
    }

    const fromSource = _findSuggestItemLabelByValue(v, props.source);

    if (fromSource) {
      setInputValue(fromSource);

      return;
    }

    if (geoMode && lastPickedRef.current?.value === v) {
      setInputValue(lastPickedRef.current.label);

      return;
    }

    setInputValue('');
  }, [field.value, props.source, geoMode, selectedRegion]);

  useEffect(() => {
    return () => {
      clearSearchTimeout();
    };
  }, [clearSearchTimeout]);

  useEffect(() => {
    const v = String(field.value);

    if (lastPickedRef.current && lastPickedRef.current.value !== v) {
      lastPickedRef.current = null;
    }

    if (geoMode && selectedRegion && String(selectedRegion.regionId) === v) {
      setInputValue(selectedRegion.displayName);

      return;
    }

    const fromSource = _findSuggestItemLabelByValue(v, props.source);

    if (fromSource) {
      setInputValue(fromSource);

      return;
    }

    if (geoMode && lastPickedRef.current?.value === v) {
      setInputValue(lastPickedRef.current.label);

      return;
    }

    setInputValue('');
  }, [field.value, props.source, geoMode, selectedRegion]);

  // Geo: не подставляем `selectedRegion` из статичного `source` (там часто одна «Москва») — иначе гонка с саджестом.
  useEffect(() => {
    if (!geoMode) {
      return;
    }

    const v = String(field.value);

    if (!v) {
      dispatch(setSelectedRegion(null));

      return;
    }

    if (String(selectedRegion?.regionId) === v) {
      return;
    }
  }, [geoMode, field.value, selectedRegion, dispatch]);

  const runRemoteSearch = useCallback(
    async (text: string) => {
      const trimmed = text.trim();

      if (trimmed.length < MIN_REGION_SUGGEST_CHARS) {
        setRemoteSuggestions([]);

        return;
      }

      const requestId = ++searchRequestIdRef.current;
      const regions = await searchRegions({ httpApi, logger, text: trimmed });

      if (requestId !== searchRequestIdRef.current) {
        return;
      }

      const nextMeta = new Map<string, number | null>();
      regions.forEach(({ id, parentId }) => {
        nextMeta.set(String(id), parentId);
      });
      catalogParentByIdRef.current = nextMeta;

      setRemoteSuggestions(
        regions.map(({ id, displayName }) => ({
          value: String(id),
          title: displayName,
          type: 'default' as const,
          rightIcon:
            String(id) === String(field.value) ? <IconFunctionalSuccess16 color="icon-main-default" /> : undefined,
        })),
      );
    },
    [httpApi, logger, field.value],
  );

  const scheduleRemoteSearch = useCallback(
    (text: string) => {
      clearSearchTimeout();
      searchTimeoutRef.current = setTimeout(() => {
        void runRemoteSearch(text);
      }, REGION_SUGGEST_DEBOUNCE_MS);
    },
    [clearSearchTimeout, runRemoteSearch],
  );

  const handleSuggestSelect = (item: ISuggestionItem<string>) => {
    const meta = catalogParentByIdRef.current;
    const parentForRef = meta.has(item.value) ? (meta.get(item.value) ?? null) : null;
    lastPickedRef.current = { value: item.value, label: item.title, parentId: parentForRef };
    setInputValue(item.title);
    setIsSuggestOpen(false);

    if (geoMode) {
      const next: ISelectedRegion = { regionId: Number(item.value), displayName: item.title };
      if (meta.has(item.value)) {
        next.parentId = meta.get(item.value) ?? null;
      }
      field.onChange(item.value);
      dispatch(setSelectedRegion(next));
      clearSearchTimeout();
      setRemoteSuggestions([]);
    } else {
      field.onChange(item.value);
    }
  };

  const handleSuggestClose = () => {
    setIsSuggestOpen(false);

    if (geoMode) {
      clearSearchTimeout();
      setRemoteSuggestions([]);
    }

    restoreCommittedDisplay();
  };

  const handleInputFocus = () => {
    setIsSuggestOpen(true);

    if (geoMode) {
      setInputValue('');
      setRemoteSuggestions([]);

      return;
    }

    setInputValue('');
  };

  const handleInputChange = (_: React.ChangeEvent<HTMLInputElement>, value: string) => {
    setInputValue(value);

    if (geoMode) {
      if (value.trim().length < MIN_REGION_SUGGEST_CHARS) {
        setRemoteSuggestions([]);
      }

      scheduleRemoteSearch(value);
    }

    if (!isSuggestOpen) {
      setIsSuggestOpen(true);
    }
  };

  const suggestions: ISuggestionDefaultItem<string>[] = useMemo(() => {
    if (geoMode) {
      const trimmed = inputValue.trim();

      if (trimmed.length < MIN_REGION_SUGGEST_CHARS) {
        const q = trimmed.toLowerCase();
        const list = q ? regionsOptions.filter(o => o.label.toLowerCase().includes(q)) : regionsOptions;

        return list.map(item => ({
          value: item.value,
          title: item.label,
          type: 'default' as const,
          rightIcon:
            item.value === String(field.value) ? <IconFunctionalSuccess16 color="icon-main-default" /> : undefined,
        }));
      }

      return remoteSuggestions.map(item => ({
        ...item,
        rightIcon:
          item.value === String(field.value) ? <IconFunctionalSuccess16 color="icon-main-default" /> : undefined,
      }));
    }

    if (!props.source) {
      return [];
    }

    return props.source
      .filter(item => item.label.toLowerCase().includes(inputValue.toLowerCase()))
      .map(item => ({
        value: item.value,
        title: item.label,
        type: 'default',
        rightIcon: item.value === field.value ? <IconFunctionalSuccess16 color="icon-main-default" /> : undefined,
      }));
  }, [geoMode, remoteSuggestions, regionsOptions, props.source, inputValue, field.value]);

  return {
    inputValue,
    error: fieldState.error,
    isError: fieldState.isDirty || fieldState.invalid,
    name: field.name,
    label: props.label,
    placeholder: props.placeholder,
    disabled: props.disabled,
    isSuggestOpen,
    handleSuggestSelect,
    handleSuggestClose,
    handleInputFocus,
    handleInputChange,
    suggestions,
  };
};
